/**
 * El buscador, disponible desde CUALQUIER pantalla.
 *
 * POR QUE ES UN CONTEXTO Y NO UN ESTADO DE PANTALLA
 * -------------------------------------------------
 * Consultar un precio pasa treinta veces por dia y casi nunca desde el inicio:
 * pasa mientras se esta mirando la caja, cargando un cliente o revisando el
 * taller. Si el buscador viviera en la pantalla de inicio, habria que navegar
 * hasta ahi para consultar — y navegar es exactamente lo que rompe el trabajo a
 * medio hacer.
 *
 * Montandolo una sola vez arriba del Outlet, se abre desde donde sea, no cambia
 * de ruta, y al cerrarse deja todo como estaba.
 */
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useRouterState } from "@tanstack/react-router";

import { BuscadorGlobal, useAtajoBuscador } from "@/components/buscador/BuscadorGlobal";
import { avisarUsadoSinPrecio, useAvisoEnTaller } from "@/components/venta/aviso-en-taller";
import {
  itemDeResultado,
  usadoSinPrecio,
  useTicket,
  type ResultadoParaTicket,
} from "@/lib/ticket-store";

interface BuscadorCtx {
  abrir: () => void;
  cerrar: () => void;
  abierto: boolean;
  /** Los numeros internos (margen, costo) se ocultan cuando el cliente ve la pantalla. */
  internos: boolean;
  alternarInternos: () => void;
}

const Ctx = createContext<BuscadorCtx | null>(null);

/**
 * En Mac el atajo es ⌘K; en Windows y Linux, Ctrl+K. Mostrar el simbolo
 * equivocado es peor que no mostrar ninguno: manda a probar una tecla que no
 * hace nada, y despues nadie vuelve a intentarlo.
 */
export const ES_MAC =
  typeof navigator !== "undefined" &&
  /mac|iphone|ipad|ipod/i.test(navigator.platform || navigator.userAgent);

export const TECLA_BUSCAR = ES_MAC ? "⌘K" : "Ctrl+K";

/**
 * LA LUPA SABE DONDE ESTA PARADA
 * ------------------------------
 * Elegir un producto en el buscador hacia siempre lo mismo: agregarlo al ticket.
 * En el mostrador esta bien, es el camino rapido de todos los dias. En
 * cualquier otra pantalla es una trampa: se busca un producto para ver el stock
 * estando en Caja o en el taller, y el sistema arranca a armar una venta que
 * nadie pidio.
 *
 * Asi que el mismo buscador se comporta distinto segun la ruta: en `/` agrega al
 * ticket; en el resto navega a la ficha de lo que se eligio. La decision se toma
 * aca, en un solo lugar, y no adentro del buscador, que no tiene por que saber
 * de rutas.
 */
export function BuscadorProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [abierto, setAbierto] = useState(false);
  const [internos, setInternos] = useState(true);
  const { agregar } = useTicket();
  const enElMostrador = useRouterState({ select: (s) => s.location.pathname === "/" });
  // Mismo aviso que el campo de carga del ticket: el buscador es el OTRO
  // camino hacia el mismo agregar, y la regla —se avisa, no se bloquea— es una
  // sola.
  const avisarEnTaller = useAvisoEnTaller();

  const abrir = useCallback(() => setAbierto(true), []);
  const cerrar = useCallback(() => setAbierto(false), []);
  const alternarInternos = useCallback(() => setInternos((v) => !v), []);

  useAtajoBuscador(abrir);

  const agregarAVenta = useCallback(
    (p: ResultadoParaTicket) => {
      // Un usado sin precio se venderia al costo: mismo freno que el ticket.
      if (usadoSinPrecio(p)) {
        avisarUsadoSinPrecio(p.nombre);
        return;
      }
      avisarEnTaller(p.producto_id);
      // Con el precio de lista del resultado, igual que el campo del ticket.
      // Antes entraba con precio 0 «porque lo resuelve el backend»: el backend
      // si, pero el ticket totalizaba con ese cero y Cobrar quedaba en $ 0,00.
      // El precio que viaja al emitir sigue siendo el del backend (solo se
      // manda si se lo edito a mano), asi que esto no congela nada.
      agregar(itemDeResultado(p));
    },
    [agregar, avisarEnTaller],
  );

  const valor = useMemo(
    () => ({ abrir, cerrar, abierto, internos, alternarInternos }),
    [abrir, cerrar, abierto, internos, alternarInternos],
  );

  return (
    <Ctx.Provider value={valor}>
      {children}
      <BuscadorGlobal
        abierto={abierto}
        onCerrar={cerrar}
        onAgregarAVenta={enElMostrador ? agregarAVenta : undefined}
      />
    </Ctx.Provider>
  );
}

export function useBuscador(): BuscadorCtx {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error("useBuscador tiene que usarse adentro de <BuscadorProvider>.");
  }
  return ctx;
}

/**
 * La tecla del atajo, lista para poner en pantalla.
 *
 * Se muestra SIEMPRE que haya teclado, y nunca en el celular: ahi el atajo no
 * existe y un `⌘K` flotando solo ocupa lugar y confunde.
 */
export function TeclaAtajo({ className }: { className?: string }): JSX.Element {
  return (
    <kbd
      className={
        className ??
        "hidden rounded-sm border border-border bg-muted px-1.5 py-0.5 text-xs text-muted-foreground sm:inline-block"
      }
    >
      {TECLA_BUSCAR}
    </kbd>
  );
}
